import { StudentData, MacroData, DimensionScore } from '../types';

export function buildMacroData(
  taskId: string,
  className: string,
  studentDataList: StudentData[]
): MacroData {
  const dimensionScores = calculateClassDimensionScores(studentDataList);
  const { redList, blackList } = findRedAndBlackList(dimensionScores);
  const weakestGroup = findWeakestGroup(studentDataList);

  return {
    taskId,
    className,
    avgScores: dimensionScores,
    redList,
    blackList,
    weakestGroup,
  };
}

function calculateClassDimensionScores(studentDataList: StudentData[]): DimensionScore[] {
  if (studentDataList.length === 0) {
    return [
      { dimension: '运动参与', avgScore: 0 },
      { dimension: '技能学习', avgScore: 0 },
      { dimension: '健康行为', avgScore: 0 },
      { dimension: '体育品德', avgScore: 0 },
    ];
  }

  const totals: Record<string, number> = {
    运动参与: 0,
    技能学习: 0,
    健康行为: 0,
    体育品德: 0,
  };

  const counts: Record<string, number> = {
    运动参与: 0,
    技能学习: 0,
    健康行为: 0,
    体育品德: 0,
  };

  for (const data of studentDataList) {
    totals.运动参与 += data.selfEval.q1Participation + data.selfEval.q2Resilience;
    counts.运动参与 += 2;

    totals.技能学习 += data.selfEval.q3SkillKnow + data.selfEval.q4SkillImprove;
    counts.技能学习 += 2;

    totals.健康行为 += data.selfEval.q5HealthSafe + data.selfEval.q6HealthPrep;
    counts.健康行为 += 2;

    totals.体育品德 += data.selfEval.q7MoralQueue + data.selfEval.q8MoralEncourage;
    counts.体育品德 += 2;
  }

  return Object.keys(totals).map((dimension) => ({
    dimension,
    avgScore: counts[dimension] > 0 ? Math.round((totals[dimension] / counts[dimension]) * 10) / 10 : 0,
  }));
}

function findRedAndBlackList(scores: DimensionScore[]): {
  redList: { question: string; avgScore: number }[];
  blackList: { question: string; avgScore: number }[];
} {
  const sorted = [...scores].sort((a, b) => b.avgScore - a.avgScore);

  const redList = sorted.slice(0, 2).map((s) => ({
    question: s.dimension,
    avgScore: s.avgScore,
  }));

  const blackList = sorted.slice(-2).reverse().map((s) => ({
    question: s.dimension,
    avgScore: s.avgScore,
  }));

  return { redList, blackList };
}

function findWeakestGroup(
  studentDataList: StudentData[]
): MacroData['weakestGroup'] | undefined {
  if (studentDataList.length === 0) return undefined;

  const groupScores: Record<string, { total: number; count: number; scores: Record<string, number> }> = {};

  for (const data of studentDataList) {
    const groupId = data.student.groupId;
    if (!groupScores[groupId]) {
      groupScores[groupId] = { total: 0, count: 0, scores: {} };
    }

    const groupScore =
      data.selfEval.q1Participation +
      data.selfEval.q2Resilience +
      data.selfEval.q3SkillKnow +
      data.selfEval.q4SkillImprove +
      data.selfEval.q5HealthSafe +
      data.selfEval.q6HealthPrep +
      data.selfEval.q7MoralQueue +
      data.selfEval.q8MoralEncourage;

    groupScores[groupId].total += groupScore;
    groupScores[groupId].count += 8;
    groupScores[groupId].scores['整体'] = groupScores[groupId].total / groupScores[groupId].count;
  }

  let weakestGroupId = '';
  let lowestAvg = Infinity;

  for (const [groupId, data] of Object.entries(groupScores)) {
    const avg = data.total / data.count;
    if (avg < lowestAvg) {
      lowestAvg = avg;
      weakestGroupId = groupId;
    }
  }

  if (!weakestGroupId) return undefined;

  const painPoints: string[] = [];
  const weakestData = groupScores[weakestGroupId];

  if (weakestData.scores['整体'] < 2.5) {
    painPoints.push('整体表现需要加强');
  }

  return {
    groupId: weakestGroupId,
    dimensionScores: weakestData.scores,
    painPoints,
  };
}